import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from "../Config/config";

function HRInterview() {
  const { applicationId } = useParams();
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (applicationId && localStorage.getItem(`terminated_${applicationId}`)) {
      localStorage.removeItem(`terminated_${applicationId}`);
      setError("terminated");
      // Notify backend if refresh was detected
      const url = `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${applicationId}`;
      const payload = JSON.stringify({ applicationId, interviewType: 'HR_BEHAVIORAL', isTerminated: true, answers: [] });
      if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    }

    async function fetchQuestions() {
      try {
        const response = await axios.get(`/api/ai-interview/context/${applicationId}`);
        if (response.data.generatedQuestions && Array.isArray(response.data.generatedQuestions)) {
          setQuestions(response.data.generatedQuestions);
        } else {
          // Fallback if no questions returned
          setQuestions([
            'Tell us about yourself',
            'Why do you want to work for our company?',
            'What are your career goals?',
          ]);
        }
      } catch (err) {
        console.error('Failed to fetch HR questions:', err);
        if (err.response?.status === 400 && err.response?.data?.message === "Interview already terminated") {
          setError('terminated');
        } else {
          setError('Failed to load interview questions.');
          // Fallback
          setQuestions([
            'Tell us about yourself',
            'Why do you want to work for our company?',
            'What are your career goals?',
          ]);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchQuestions();
  }, [applicationId]);

  useEffect(() => {
    const handleUnload = () => {
      if (isSubmittingRef.current || loading) return;

      // 🔥 Force termination on refresh/close
      localStorage.setItem(`terminated_${applicationId}`, 'true');

      const url = `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${applicationId}`;
      const payload = JSON.stringify({
        applicationId,
        interviewType: 'HR_BEHAVIORAL',
        isTerminated: true,
        answers: questions.map((q, index) => ({
          question: typeof q === "object" ? (q?.text || q?.question || "") : q,
          answer: answers[index] || '',
        })),
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, payload);
      } else {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        });
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [applicationId, questions, answers, loading]);

  async function handleSubmit() {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setSubmitting(true);
    try {
      await axios.post(`/api/hr-interview/${applicationId}/submit`, { answers });
      alert('HR interview submitted successfully!');
      navigate('/candidate/dashboard');
    } catch (err) {
      console.error('Failed to submit HR interview:', err);
      setError('Failed to submit interview. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (error === 'terminated') {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography variant="h4" gutterBottom>Interview Already Terminated</Typography>
        <Button variant="contained" onClick={() => navigate("/candidate/dashboard")}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          HR Interview
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          Answer the following HR questions
        </Alert>

        {questions.map((question, index) => (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Question {index + 1}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {typeof question === "object" ? (question?.text || question?.question || "") : (() => {
                try { const p = JSON.parse(question); return p?.text || p?.question || question; } catch { return question; }
              })()}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={answers[index] || ''}
              onChange={(e) => setAnswers({ ...answers, [index]: e.target.value })}
              placeholder="Your answer..."
              sx={{ mt: 1 }}
            />
          </Box>
        ))}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitting || questions.length === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Interview'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default HRInterview;
